import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, X, Upload, Check, ShieldCheck, LogOut } from "lucide-react";

// Plain axios (not the ERP `api` client) so a visitor with an old token is never sent to /login.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (id) => `${API}/site/fabric-images/${id}`;
const SITE_KEY = "nayara_site_token"; // separate website-admin login (not the ERP)
const authHeaders = () => {
  const t = localStorage.getItem(SITE_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const KINDS = {
  fabric: { cats: ["Kurti", "Dress", "Event", "Backdrop"], eyebrow: "Just arrived · Trending now", title: "New", script: "Arrivals",
    blurb: "Fresh prints and patterns from our Tiruppur studio, added as soon as they leave the press.", add: "Add new fabric", btn: "Add Fabric", noun: "fabric" },
  pattern: { cats: ["Floral", "Geometric", "Ethnic", "Abstract"], eyebrow: "Design library · Trending patterns", title: "New", script: "Patterns",
    blurb: "Our latest print designs — pick a pattern and we will print it on the fabric of your choice.", add: "Add new pattern", btn: "Add Pattern", noun: "pattern" },
};
const WHATSAPP = "919597888978";
const inr = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "");

function useFonts() {
  useEffect(() => {
    if (document.getElementById("ny-script-font")) return;
    const l = document.createElement("link");
    l.id = "ny-script-font";
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Pinyon+Script&family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&display=swap";
    document.head.appendChild(l);
  }, []);
}

export default function NewArrivals({ variant = "home", kind = "fabric" }) {
  const K = KINDS[kind] || KINDS.fabric;
  const CATS = K.cats;
  useFonts();
  const [items, setItems] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [cat, setCat] = useState("All");
  const [modal, setModal] = useState(null); // null | {mode:'add'} | {mode:'edit', item}
  const [showLogin, setShowLogin] = useState(() => kind === "fabric" && new URLSearchParams(window.location.search).has("admin"));

  const load = () =>
    axios.get(`${API}/site/fabrics`, { params: { kind, limit: variant === "home" ? 8 : 200 } })
      .then((r) => setItems(r.data)).catch(() => setItems([]));

  const checkAdmin = () => {
    if (!localStorage.getItem(SITE_KEY) || !new URLSearchParams(window.location.search).has("admin")) { setIsAdmin(false); return; }
    axios.get(`${API}/site/admin/me`, { headers: authHeaders() })
      .then(() => { setIsAdmin(true); setShowLogin(false); })
      .catch(() => { localStorage.removeItem(SITE_KEY); setIsAdmin(false); });
  };
  const logout = () => { localStorage.removeItem(SITE_KEY); setIsAdmin(false); };

  useEffect(() => { load(); checkAdmin(); }, []); // eslint-disable-line

  const shown = useMemo(() => (items || []).filter((f) => cat === "All" || f.category === cat), [items, cat]);

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.name}" from the website?`)) return;
    await axios.delete(`${API}/site/fabrics/${f.id}`, { headers: authHeaders() });
    load();
  };

  if (!isAdmin && !showLogin && items && items.length === 0) return null; // nothing to show visitors yet

  return (
    <section className="relative bg-[#FBF7F1] text-[#3A2416]" id={kind === "pattern" ? "new-patterns" : "new-arrivals"} data-testid={`new-${kind}s`}>
      {isAdmin && (
        <div className="flex items-center justify-between bg-[#2B1A10] px-6 py-3 text-[11px] uppercase tracking-[0.24em] text-[#E6D2B5] lg:px-12">
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D9B98E]" />Website admin · visible only to you</span>
          {<button onClick={logout} className="flex items-center gap-2 uppercase tracking-[0.24em] hover:text-white"><LogOut className="h-4 w-4" />Logout</button>}
        </div>
      )}
      <div className="mx-auto max-w-[1400px] px-6 pb-10 pt-24 lg:px-12 lg:pb-12 lg:pt-28">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1 }}>
            <div className="flex items-center gap-3 text-[10.5px] uppercase tracking-[0.4em] text-[#7B4520]">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#B5543A] opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#B5543A]" /></span>
              {K.eyebrow}
            </div>
            <h2 className="mt-4 text-5xl font-light leading-none text-[#2B1A10] lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {K.title} <span className="text-[#7B4520]" style={{ fontFamily: "'Pinyon Script', cursive", fontSize: "1.15em" }}>{K.script}</span>
            </h2>
            <p className="mt-4 max-w-md text-[15px] font-light leading-relaxed text-[#5E4634]">{K.blurb}</p>
          </motion.div>
          <div className="flex flex-wrap items-center gap-2">
            {["All", ...CATS].map((c) => (
              <button key={c} onClick={() => setCat(c)}
                className={`rounded-full border px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.18em] transition-all duration-500 ${cat === c ? "border-[#2B1A10] bg-[#2B1A10] text-[#FBF7F1]" : "border-[#7B4520]/35 text-[#3A2416] hover:border-[#2B1A10]"}`}>{c}</button>
            ))}
            {isAdmin && (
              <button onClick={() => setModal({ mode: "add" })} data-testid="add-fabric-btn"
                className="ml-2 inline-flex items-center gap-2 rounded-full bg-[#7B4520] px-6 py-3 text-[11px] font-medium uppercase tracking-[0.18em] text-[#FBF7F1] transition-transform duration-500 hover:-translate-y-0.5">
                <Plus className="h-4 w-4" />{K.btn}</button>
            )}
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-4">
          {isAdmin && (
            <motion.button onClick={() => setModal({ mode: "add" })} aria-label={K.add}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="group flex min-h-[436px] flex-col items-center justify-center gap-5 border-[1.5px] border-dashed border-[#7B4520]/45 transition-colors duration-500 hover:border-[#7B4520] hover:bg-[#F3EADD]">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#7B4520] text-[#FBF7F1] shadow-[0_0_0_10px_rgba(123,69,32,0.08)] transition-transform duration-700 group-hover:rotate-90">
                <Plus className="h-8 w-8" strokeWidth={1.4} />
              </span>
              <span className="text-2xl text-[#2B1A10]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{K.add}</span>
              <span className="max-w-[200px] text-center text-xs leading-relaxed text-[#8A6E58]">Upload photos of a trending {K.noun}</span>
            </motion.button>
          )}

          {items === null && Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-[436px] animate-pulse bg-[#EFE4D4]" />)}

          {shown.map((f, i) => (
            <motion.div key={f.id} initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9, delay: (i % 4) * 0.12, ease: [0.2, 0.7, 0.2, 1] }} className="group flex flex-col gap-4">
              <div className={`relative ${kind === "pattern" ? "h-[320px]" : "h-[380px]"} overflow-hidden bg-[#EFE4D4] ${i % 4 === 1 ? "rounded-t-[160px]" : ""}`}>
                <img src={imgUrl(f.images?.[0])} alt={f.name} loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#2B1A10]/50" />
                <span className="absolute left-4 top-4 bg-[#FBF7F1]/90 px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] text-[#7B4520]">{f.category}</span>
                {f.is_new && !isAdmin && <span className="absolute right-4 top-4 bg-[#B5543A] px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] text-white">New</span>}
                {isAdmin ? (
                  <div className="absolute right-3 top-3 flex gap-2">
                    <button aria-label="Edit fabric" onClick={() => setModal({ mode: "edit", item: f })} className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FBF7F1] text-[#2B1A10] transition-colors hover:bg-[#2B1A10] hover:text-[#FBF7F1]"><Pencil className="h-4 w-4" /></button>
                    <button aria-label="Delete fabric" onClick={() => remove(f)} className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FBF7F1] text-[#9B2F1E] transition-colors hover:bg-[#9B2F1E] hover:text-white"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi, I'm interested in ${f.name} (${f.category})`)}`} target="_blank" rel="noreferrer"
                    className="absolute inset-x-4 bottom-4 translate-y-3 bg-[#FBF7F1] py-3.5 text-center text-[11px] uppercase tracking-[0.24em] text-[#2B1A10] opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">Enquire on WhatsApp</a>
                )}
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-2xl text-[#2B1A10]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{f.name}</div>
                {f.price > 0 && <div className="whitespace-nowrap text-[13px] text-[#7B4520]"><span className="font-semibold">{inr(f.price)}</span> / m</div>}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {modal && <FabricModal key="m" kind={kind} K={K} modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
        {showLogin && !isAdmin && <SiteLogin key="l" onClose={() => setShowLogin(false)} onDone={checkAdmin} />}
      </AnimatePresence>
    </section>
  );
}

export function FabricModal({ modal, onClose, onSaved, kind, K }) {
  const CATS = K.cats;
  const edit = modal.mode === "edit";
  const it = modal.item || {};
  const [name, setName] = useState(it.name || "");
  const [price, setPrice] = useState(it.price || "");
  const [category, setCategory] = useState(it.category || CATS[0]);
  const [isNew, setIsNew] = useState(it.is_new ?? true);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);

  const save = async () => {
    setErr("");
    if (!name.trim()) return setErr("Name venum");
    if (!edit && files.length === 0) return setErr("Kuraindhadhu oru photo venum");
    setBusy(true);
    try {
      if (edit) {
        await axios.put(`${API}/site/fabrics/${it.id}`, { name, category, price: Number(price) || 0, is_new: isNew }, { headers: authHeaders() });
      } else {
        const fd = new FormData();
        fd.append("name", name); fd.append("category", category);
        fd.append("price", String(Number(price) || 0)); fd.append("is_new", String(isNew)); fd.append("kind", kind);
        files.forEach((f) => fd.append("images", f));
        await axios.post(`${API}/site/fabrics`, fd, { headers: authHeaders() });
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save aagala, thirumba try pannunga");
    } finally { setBusy(false); }
  };

  return (
    <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#2B1A10]/55 p-4 backdrop-blur-md"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div role="dialog" aria-label={edit ? `Edit ${K.noun}` : K.add} onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
        className="max-h-[92vh] w-full max-w-[760px] overflow-y-auto bg-[#FBF7F1] text-[#3A2416] shadow-[0_60px_120px_-40px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-between border-b border-[#7B4520]/15 px-6 pb-5 pt-7 sm:px-10">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.36em] text-[#7B4520]">{edit ? "Edit" : "New arrival"}</div>
            <div className="mt-1 text-4xl text-[#2B1A10]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {edit ? "Edit" : "Add a"} <span className="text-[#7B4520]" style={{ fontFamily: "'Pinyon Script', cursive" }}>{K.noun}</span></div>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full border border-[#7B4520]/30"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex flex-col gap-6 px-6 py-7 sm:px-10">
          {!edit && (
            <div className="flex flex-col gap-2.5">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#5E4634]">Photos</span>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <label className="flex h-[118px] cursor-pointer flex-col items-center justify-center gap-2 border-[1.5px] border-dashed border-[#7B4520]/50 bg-[#F3EADD] text-center text-xs text-[#7B4520] transition-colors hover:bg-[#EFE2CF]">
                  <Upload className="h-6 w-6" strokeWidth={1.4} />Drop or browse<br />JPG / PNG
                  <input type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => setFiles((p) => [...p, ...Array.from(e.target.files || [])].slice(0, 8))} />
                </label>
                {previews.map((src, i) => (
                  <div key={src} className="relative h-[118px] overflow-hidden">
                    <img src={src} alt="" className="h-full w-full object-cover" />
                    {i === 0 && <span className="absolute bottom-1.5 left-1.5 bg-[#FBF7F1] px-2 py-0.5 text-[9px] uppercase tracking-[0.16em] text-[#7B4520]">Cover</span>}
                    <button aria-label="Remove photo" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}
                      className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#FBF7F1]/90"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <label className="flex flex-col gap-2.5 text-[11px] uppercase tracking-[0.22em] text-[#5E4634]">{kind === "pattern" ? "Pattern name" : "Fabric name"}
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ajrakh Weave"
                className="h-[50px] border border-[#7B4520]/30 bg-white px-4 text-base normal-case tracking-normal text-[#2B1A10] outline-none focus:border-[#7B4520]" />
            </label>
            <label className="flex flex-col gap-2.5 text-[11px] uppercase tracking-[0.22em] text-[#5E4634]">Price per meter
              <span className="flex h-[50px] items-center border border-[#7B4520]/30 bg-white focus-within:border-[#7B4520]">
                <span className="flex h-full items-center border-r border-[#7B4520]/20 px-3.5 text-base text-[#7B4520]">₹</span>
                <input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0"
                  className="h-full flex-1 bg-transparent px-3.5 text-base text-[#2B1A10] outline-none" />
                <span className="px-3.5 text-[13px] normal-case tracking-normal text-[#8A6E58]">/ m</span>
              </span>
            </label>
          </div>

          <div className="flex flex-col gap-2.5">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#5E4634]">Category</span>
            <div className="flex flex-wrap gap-2.5">
              {CATS.map((c) => (
                <button key={c} onClick={() => setCategory(c)}
                  className={`rounded-full border px-6 py-3 text-xs font-medium uppercase tracking-[0.16em] transition-all duration-300 ${category === c ? "border-[#2B1A10] bg-[#2B1A10] text-[#FBF7F1]" : "border-[#7B4520]/35 text-[#3A2416]"}`}>{c}</button>
              ))}
            </div>
          </div>
          {err && <div className="border border-[#9B2F1E]/30 bg-[#9B2F1E]/5 px-4 py-3 text-sm text-[#9B2F1E]">{err}</div>}
        </div>

        <div className="flex flex-col-reverse gap-4 border-t border-[#7B4520]/15 px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-10">
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-[#5E4634]">
            <input type="checkbox" checked={isNew} onChange={(e) => setIsNew(e.target.checked)} className="h-[18px] w-[18px] accent-[#7B4520]" />Show “New” tag on website</label>
          <div className="flex gap-3">
            <button onClick={onClose} className="border border-[#7B4520]/35 px-6 py-3.5 text-xs font-medium uppercase tracking-[0.18em]">Cancel</button>
            <button onClick={save} disabled={busy}
              className="inline-flex items-center gap-2 bg-[#7B4520] px-7 py-3.5 text-xs font-medium uppercase tracking-[0.18em] text-[#FBF7F1] disabled:opacity-60">
              <Check className="h-4 w-4" />{busy ? "Saving..." : edit ? "Save changes" : "Publish to website"}</button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function SiteLogin({ onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/site/admin/login`, { email, password });
      localStorage.setItem(SITE_KEY, data.token);
      onDone();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Login aagala");
    } finally { setBusy(false); }
  };
  return (
    <motion.div className="fixed inset-0 z-[110] flex items-center justify-center bg-[#2B1A10]/60 p-4 backdrop-blur-md"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.form onSubmit={submit} onClick={(e) => e.stopPropagation()} aria-label="Website admin login"
        initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
        className="w-full max-w-[440px] bg-[#FBF7F1] px-8 py-10 text-[#3A2416] shadow-[0_60px_120px_-40px_rgba(0,0,0,0.6)] sm:px-10">
        <div className="text-center">
          <div className="text-[10.5px] uppercase tracking-[0.36em] text-[#7B4520]">Nayara Studio</div>
          <div className="mt-2 text-4xl text-[#2B1A10]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Website <span className="text-[#7B4520]" style={{ fontFamily: "'Pinyon Script', cursive" }}>admin</span></div>
        </div>
        <div className="mt-8 flex flex-col gap-5">
          <label className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5E4634]">Email
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username"
              className="h-[50px] border border-[#7B4520]/30 bg-white px-4 text-base normal-case tracking-normal text-[#2B1A10] outline-none focus:border-[#7B4520]" /></label>
          <label className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5E4634]">Password
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
              className="h-[50px] border border-[#7B4520]/30 bg-white px-4 text-base normal-case tracking-normal text-[#2B1A10] outline-none focus:border-[#7B4520]" /></label>
          {err && <div className="border border-[#9B2F1E]/30 bg-[#9B2F1E]/5 px-4 py-3 text-sm text-[#9B2F1E]">{err}</div>}
          <button type="submit" disabled={busy} className="mt-2 h-[52px] bg-[#7B4520] text-xs font-medium uppercase tracking-[0.22em] text-[#FBF7F1] disabled:opacity-60">{busy ? "Signing in..." : "Sign in"}</button>
        </div>
      </motion.form>
    </motion.div>
  );
}
